import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { QueueExplanation } from './QueueExplanation';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Clock, 
  CheckCircle, 
  AlertTriangle, 
  Users, 
  Trophy,
  Timer,
  Eye,
  Play
} from 'lucide-react';
import { useUserRole } from '@/hooks/useUserRole';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

interface QueueItem {
  id: string;
  type: 'game_approval' | 'scorekeeper_application' | 'team_confirmation' | 'player_status' | 'score_submission' | 'view_game_details';
  title: string;
  description: string;
  game_id?: string;
  application_id?: string;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  due_date?: string;
  action_required: string;
  action_url: string;
  created_at: string;
  game_data?: any;
}

export const RoleBasedQueue: React.FC = () => {
  const { permissions, userId } = useUserRole();
  const [queueItems, setQueueItems] = useState<QueueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (userId) {
      loadQueueItems();
    }
  }, [userId, permissions]);

  // Set up real-time subscriptions for queue updates
  useEffect(() => {
    if (!userId) return;

    const channels = [];

    // Subscribe to venue_games changes
    const venueGamesChannel = supabase
      .channel('venue-games-queue-updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'venue_games'
        },
        (payload) => {
          console.log('🔄 QUEUE: Venue game update detected:', payload);
          loadQueueItems();
        }
      )
      .subscribe();

    // Subscribe to games changes
    const gamesChannel = supabase
      .channel('games-queue-updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'games'
        },
        (payload) => {
          console.log('🔄 QUEUE: Game update detected:', payload);
          loadQueueItems();
        }
      )
      .subscribe();

    // Subscribe to team changes
    const teamsChannel = supabase
      .channel('teams-queue-updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'teams'
        },
        (payload) => {
          console.log('🔄 QUEUE: Team update detected:', payload);
          loadQueueItems();
        }
      )
      .subscribe();

    channels.push(venueGamesChannel, gamesChannel, teamsChannel);

    return () => {
      channels.forEach(channel => supabase.removeChannel(channel));
    };
  }, [userId]);

  const loadQueueItems = async () => {
    if (!userId) return;

    setLoading(true);
    try {
      console.log('🔄 QUEUE: Loading queue items for user:', userId, 'Role:', permissions.role);
      const items: QueueItem[] = [];

      // Load different queue items based on role
      if (permissions.isCoach || permissions.isTeamOwner) {
        await loadTeamManagerQueue(items);
      }

      if (permissions.role === 'scorekeeper') {
        await loadScorekeeperQueue(items);
      }

      if (permissions.isPlayer) {
        await loadPlayerQueue(items);
      }

      if (permissions.canManageVenues) {
        await loadVenueManagerQueue(items);
      }

      console.log('🔄 QUEUE: Total items loaded:', items.length);
      console.log('🔄 QUEUE: Urgent items:', items.filter(i => i.priority === 'urgent').length);

      // Sort by priority and due date
      items.sort((a, b) => {
        const priorityOrder = { urgent: 0, high: 1, normal: 2, low: 3 };
        const priorityDiff = priorityOrder[a.priority] - priorityOrder[b.priority];
        if (priorityDiff !== 0) return priorityDiff;
        
        if (a.due_date && b.due_date) {
          return new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
        }
        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      });

      setQueueItems(items);
    } catch (error) {
      console.error('Error loading queue items:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTeamManagerQueue = async (items: QueueItem[]) => {
    console.log('🔄 TEAM MANAGER QUEUE: Loading for user:', userId);
    
    // Get teams managed by this user (both as creator and coach)
    const { data: managedTeams } = await supabase
      .from('teams')
      .select('team_id, team_name')
      .eq('created_by', userId);

    const { data: coachedTeams } = await supabase
      .from('team_coaches')
      .select('team_id')
      .eq('coach_id', userId);

    const teamIds = [
      ...(managedTeams?.map(t => t.team_id) || []),
      ...(coachedTeams?.map(t => t.team_id) || [])
    ];

    console.log('🔄 TEAM MANAGER QUEUE: Managed/coached team IDs:', teamIds);

    // 1. Regular games awaiting approval
    const { data: pendingGames } = await supabase
      .from('games')
      .select(`
        *,
        home_team:teams!games_home_team_id_fkey(team_id, team_name),
        away_team:teams!games_away_team_id_fkey(team_id, team_name)
      `)
      .eq('game_status', 'pending')
      .or(`created_by.eq.${userId}`);

    pendingGames?.forEach(game => {
      const gameDateTime = new Date(`${game.game_date} ${game.game_time || '00:00'}`);
      const now = new Date();
      const isPastDue = gameDateTime < now;
      
      // Don't show past games in actionable queue items unless they're urgent
      if (isPastDue) {
        const hoursAfterGame = (now.getTime() - gameDateTime.getTime()) / (1000 * 60 * 60);
        
        // Only show recently missed games (within 24 hours) as urgent
        if (hoursAfterGame <= 24) {
          items.push({
            id: `game_approval_missed_${game.id}`,
            type: 'view_game_details',
            title: 'Missed Game - Past Due',
            description: `${game.game_name} on ${format(new Date(game.game_date), 'MMM dd, yyyy')} - OVERDUE`,
            game_id: game.id,
            priority: 'urgent',
            due_date: game.game_date,
            action_required: 'Review missed game',
            action_url: `/games/${game.id}/details`,
            created_at: game.created_at,
            game_data: { ...game, isPastDue: true }
          });
        }
        return; // Skip adding as normal approval item
      }
      
      items.push({
        id: `game_approval_${game.id}`,
        type: 'game_approval',
        title: 'Game Awaiting Approval',
        description: `${game.game_name} on ${format(new Date(game.game_date), 'MMM dd, yyyy')}`,
        game_id: game.id,
        priority: 'normal',
        due_date: game.game_date,
        action_required: 'Review and submit for approval',
        action_url: `/games/${game.id}/approval`,
        created_at: game.created_at,
        game_data: game
      });
    });

    // 2. Venue games for managed teams
    if (teamIds.length > 0) {
      // Pending venue games
      let pendingVenueGames = null;
      if (teamIds.length > 0) {
        const { data } = await supabase
          .from('venue_games')
          .select(`
            *,
            home_team:teams!venue_games_home_team_id_fkey(team_id, team_name, team_logo),
            away_team:teams!venue_games_away_team_id_fkey(team_id, team_name, team_logo)
          `)
          .eq('game_status', 'pending')
          .or(`home_team_id.in.(${teamIds.join(',')}),away_team_id.in.(${teamIds.join(',')})`);
        pendingVenueGames = data;
      }

      console.log('🔄 TEAM MANAGER QUEUE: Found pending venue games:', pendingVenueGames?.length || 0);

      pendingVenueGames?.forEach(game => {
        const homeTeamName = game.home_team?.team_name || 'TBD';
        const awayTeamName = game.away_team?.team_name || 'TBD';
        const gameTitle = `${homeTeamName} vs ${awayTeamName}`;
        
        const gameDateTime = new Date(`${game.game_date} ${game.game_time || '00:00'}`);
        const now = new Date();
        const isPastDue = gameDateTime < now;
        
        // Handle past due venue games differently
        if (isPastDue) {
          const hoursAfterGame = (now.getTime() - gameDateTime.getTime()) / (1000 * 60 * 60);
          
          // Only show recently missed games (within 24 hours) as urgent
          if (hoursAfterGame <= 24) {
            items.push({
              id: `venue_game_missed_${game.id}`,
              type: 'view_game_details',
              title: 'Missed Game - Past Due',
              description: `${gameTitle} on ${format(new Date(game.game_date), 'MMM dd, yyyy')} - OVERDUE - ${game.game_code || 'No Code'}`,
              game_id: game.id,
              priority: 'urgent',
              due_date: game.game_date,
              action_required: 'Review missed game',
              action_url: `/games/${game.id}/details`,
              created_at: game.created_at,
              game_data: {
                ...game,
                formatted_title: gameTitle,
                isPastDue: true
              }
            });
          }
          return; // Skip adding as normal confirmation item
        }
        
        items.push({
          id: `venue_game_pending_${game.id}`,
          type: 'team_confirmation',
          title: 'Team Confirmation Required',
          description: `${gameTitle} on ${format(new Date(game.game_date), 'MMM dd, yyyy')} - ${game.game_code || 'No Code'}`,
          game_id: game.id,
          priority: 'high',
          due_date: game.game_date,
          action_required: 'Confirm team participation',
          action_url: `/game-management`,
          created_at: game.created_at,
          game_data: {
            ...game,
            formatted_title: gameTitle
          }
        });
      });

      // Confirmed venue games with view details option
      let confirmedVenueGames = null;
      if (teamIds.length > 0) {
        const { data } = await supabase
          .from('venue_games')
          .select(`
            *,
            home_team:teams!venue_games_home_team_id_fkey(team_id, team_name, team_logo),
            away_team:teams!venue_games_away_team_id_fkey(team_id, team_name, team_logo)
          `)
          .eq('game_status', 'confirmed')
          .or(`home_team_id.in.(${teamIds.join(',')}),away_team_id.in.(${teamIds.join(',')})`);
        confirmedVenueGames = data;
      }

      console.log('🔄 TEAM MANAGER QUEUE: Found confirmed venue games:', confirmedVenueGames?.length || 0);

      confirmedVenueGames?.forEach(game => {
        console.log('🔄 TEAM MANAGER QUEUE: Processing confirmed game:', game.game_code, game.game_title);
        
        const homeTeamName = game.home_team?.team_name || 'TBD';
        const awayTeamName = game.away_team?.team_name || 'TBD';
        const gameTitle = `${homeTeamName} vs ${awayTeamName}`;
        
        // Default roster deadline if not set (30 minutes before game)
        const gameDateTime = new Date(`${game.game_date} ${game.game_time}`);
        const defaultRosterDeadline = new Date(gameDateTime.getTime() - 30 * 60 * 1000);
        const rosterDeadline = game.roster_deadline ? new Date(game.roster_deadline) : defaultRosterDeadline;
        
        const now = new Date();
        const hoursUntilDeadline = (rosterDeadline.getTime() - now.getTime()) / (1000 * 60 * 60);
        const hoursUntilGame = (gameDateTime.getTime() - now.getTime()) / (1000 * 60 * 60);
        
        // Check if game is missed (past game time but within 72 hours)
        if (hoursUntilGame < 0 && hoursUntilGame > -72) {
          // This is a missed game - show as urgent
          items.push({
            id: `missed_game_${game.id}`,
            type: 'view_game_details',
            title: 'Missed Game',
            description: `${gameTitle} on ${format(new Date(game.game_date), 'MMM dd, yyyy')} - MISSED - ${game.game_code || 'No Code'}`,
            game_id: game.id,
            priority: 'urgent',
            due_date: game.game_date,
            action_required: 'Review missed game',
            action_url: `/games/${game.id}/details`,
            created_at: game.updated_at,
            game_data: {
              ...game,
              formatted_title: gameTitle,
              status: 'missed'
            }
          });
          return;
        }
        
        // Skip games that have been expired for more than 72 hours
        if (hoursUntilGame < -72) {
          console.log('🔄 TEAM MANAGER QUEUE: Skipping expired game:', game.game_code, 'Hours past:', Math.abs(hoursUntilGame));
          return;
        }
        
        // Determine priority and title based on game timing
        let priority: 'low' | 'normal' | 'high' | 'urgent' = 'normal';
        let title = 'Confirmed Game - View Details';
        let actionRequired = 'View Details';
        
        // Game has ended (within 3 hours) - show as completed
        if (hoursUntilGame < 0) {
          title = 'Game Completed - View Details';
          actionRequired = 'View Details';
          priority = 'low';
        }
        // Game starting very soon
        else if (hoursUntilGame <= 2) {
          title = 'Game Starting Soon - View Details';
          priority = 'urgent';
        } 
        // Roster deadline approaching or game within 24 hours
        else if (hoursUntilDeadline <= 24 || hoursUntilGame <= 24) {
          title = 'Upcoming Game - View Details';
          priority = 'high';
        } 
        // Game within 48 hours
        else if (hoursUntilGame <= 48) {
          title = 'Upcoming Game - View Details';
          priority = 'high';
        } 
        // Future game
        else {
          title = 'Confirmed Game - View Details';
          priority = 'normal';
        }
        
        console.log('🔄 TEAM MANAGER QUEUE: Game priority:', priority, 'Hours until game:', hoursUntilGame, 'Title:', title);
        
        items.push({
          id: `venue_game_details_${game.id}`,
          type: 'view_game_details',
          title,
          description: `${gameTitle} on ${format(new Date(game.game_date), 'MMM dd, yyyy')} - ${game.game_code || 'No Code'}`,
          game_id: game.id,
          priority,
          due_date: game.roster_deadline || game.game_date,
          action_required: actionRequired,
          action_url: `/games/${game.id}/details`,
          created_at: game.updated_at,
          game_data: game
        });
      });
    }

    // 3. Scorekeeper applications
    const { data: applications } = await supabase
      .from('scorekeeper_applications')
      .select(`
        *
      `)
      .eq('status', 'pending');

    // Process applications and get game info separately
    if (applications) {
      for (const app of applications) {
        // Try to get game info from games table first
        let gameInfo = null;
        const { data: gameData } = await supabase
          .from('games')
          .select('id, game_name, game_date')
          .eq('id', app.game_id)
          .single();
          
        if (gameData) {
          gameInfo = gameData;
        } else {
          // Try venue_games table
          const { data: venueGameData } = await supabase
            .from('venue_games')
            .select('id, game_title as game_name, game_date')
            .eq('id', app.game_id)
            .single();
          gameInfo = venueGameData;
        }
        
        if (gameInfo) {
          items.push({
            id: `scorekeeper_app_${app.id}`,
            type: 'scorekeeper_application',
            title: 'Scorekeeper Application',
            description: `Application for ${gameInfo.game_name}`,
            game_id: app.game_id,
            application_id: app.id,
            priority: 'high',
            action_required: 'Review application',
            action_url: `/games/${app.game_id}/scorekeeper-application?applicationId=${app.id}`,
            created_at: app.applied_at,
            game_data: gameInfo
          });
        }
      }
    }

    // 4. Games ready to start
    const { data: readyGames } = await supabase
      .from('games')
      .select(`
        *,
        home_team:teams!games_home_team_id_fkey(team_id, team_name),
        away_team:teams!games_away_team_id_fkey(team_id, team_name)
      `)
      .eq('game_status', 'ready_to_play');

    readyGames?.forEach(game => {
      items.push({
        id: `ready_game_${game.id}`,
        type: 'score_submission',
        title: 'Game Ready to Start',
        description: `${game.game_name} - Both teams ready`,
        game_id: game.id,
        priority: 'urgent',
        due_date: game.game_date,
        action_required: 'Start game',
        action_url: `/games/${game.id}/start`,
        created_at: game.updated_at,
        game_data: game
      });
    });
  };

  const loadScorekeeperQueue = async (items: QueueItem[]) => {
    const currentDate = new Date().toISOString();
    
    // 1. Games open for application (only future/active games)
    const { data: openGames } = await supabase
      .from('games')
      .select(`
        *,
        home_team:teams!games_home_team_id_fkey(team_id, team_name),
        away_team:teams!games_away_team_id_fkey(team_id, team_name)
      `)
      .eq('game_status', 'approved')
      .not('game_status', 'in', '(completed,cancelled,forfeited)')
      .gte('game_date', currentDate.split('T')[0]) // Only future games
      .not('id', 'in', 
        `(SELECT game_id FROM scorekeeper_applications WHERE scorekeeper_id = '${userId}')`
      );

    openGames?.forEach(game => {
      items.push({
        id: `open_game_${game.id}`,
        type: 'scorekeeper_application',
        title: 'Game Available',
        description: `Score ${game.game_name} on ${format(new Date(game.game_date), 'MMM dd, yyyy')}`,
        game_id: game.id,
        priority: 'normal',
        due_date: game.game_date,
        action_required: 'Apply to score',
        action_url: `/games/${game.id}/scorekeeper-application`,
        created_at: game.created_at,
        game_data: game
      });
    });

    // 2. Assigned games
    const { data: assignedGames } = await supabase
      .from('scorekeeper_applications')
      .select(`
        *,
        game:games(
          *,
          home_team:teams!games_home_team_id_fkey(team_id, team_name),
          away_team:teams!games_away_team_id_fkey(team_id, team_name)
        )
      `)
      .eq('scorekeeper_id', userId)
      .eq('status', 'approved');

    assignedGames?.forEach(app => {
      // Check if the Supabase query has game data and it's not null
      if (app?.game && typeof app.game === 'object' && !Array.isArray(app.game)) {
        const game = app.game as any;
        if (game?.id) {
          items.push({
            id: `assigned_game_${game.id}`,
            type: 'score_submission',
            title: 'Assigned Game',
            description: `Score ${game.game_name || 'Game'}`,
            game_id: game.id,
            priority: 'high',
            due_date: game.game_date,
            action_required: 'Score game',
            action_url: `/games/${game.id}/score`,
            created_at: app.approved_at || app.applied_at,
            game_data: game
          });
        }
      }
    });
  };

  const loadPlayerQueue = async (items: QueueItem[]) => {
    // Player confirmations and game invites
    const { data: participations } = await supabase
      .from('game_participants')
      .select('*')
      .eq('player_id', userId)
      .eq('participation_status', 'requested');

    // For each participation, fetch game details from both tables
    for (const participation of participations || []) {
      let gameData = null;
      
      // Try to get game from games table first
      const { data: gameFromGames } = await supabase
        .from('games')
        .select(`
          *,
          home_team:teams!games_home_team_id_fkey(team_id, team_name),
          away_team:teams!games_away_team_id_fkey(team_id, team_name)
        `)
        .eq('id', participation.game_id)
        .maybeSingle();
      
      if (gameFromGames) {
        gameData = gameFromGames;
      } else {
        // Try venue_games table
        const { data: gameFromVenue } = await supabase
          .from('venue_games')
          .select(`
            *,
            home_team:teams!venue_games_home_team_id_fkey(team_id, team_name),
            away_team:teams!venue_games_away_team_id_fkey(team_id, team_name)
          `)
          .eq('id', participation.game_id)
          .maybeSingle();
        
        gameData = gameFromVenue;
      }

      if (gameData) {
        items.push({
          id: `player_confirm_${participation.id}`,
          type: 'player_status',
          title: 'Game Invitation',
          description: `Confirm participation in ${gameData.game_name || gameData.game_title || 'Game'}`,
          game_id: gameData.id,
          priority: 'normal',
          due_date: gameData.game_date,
          action_required: 'Confirm participation',
          action_url: `/games/${gameData.id}/confirmation`,
          created_at: participation.created_at,
          game_data: gameData
        });
      }
    }
  };

  const loadVenueManagerQueue = async (items: QueueItem[]) => {
    // Games pending venue approval
    const { data: venueGames } = await supabase
      .from('venue_games')
      .select('*')
      .eq('venue_id', userId)
      .eq('status', 'pending');

    venueGames?.forEach(game => {
      items.push({
        id: `venue_approval_${game.id}`,
        type: 'game_approval',
        title: 'Venue Approval Required',
        description: `${game.game_title} on ${format(new Date(game.game_date), 'MMM dd, yyyy')}`,
        game_id: game.id,
        priority: 'high',
        due_date: game.game_date,
        action_required: 'Approve or reject',
        action_url: `/venue-management`,
        created_at: game.created_at,
        game_data: game
      });
    });
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'urgent': return <AlertTriangle className="h-4 w-4 text-red-500" />;
      case 'high': return <Timer className="h-4 w-4 text-orange-500" />;
      case 'normal': return <Clock className="h-4 w-4 text-blue-500" />;
      case 'low': return <Clock className="h-4 w-4 text-gray-500" />;
      default: return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-500/10 text-red-500 border-red-500';
      case 'high': return 'bg-orange-500/10 text-orange-500 border-orange-500';
      case 'normal': return 'bg-blue-500/10 text-blue-500 border-blue-500';
      case 'low': return 'bg-gray-500/10 text-gray-500 border-gray-500';
      default: return 'bg-gray-500/10 text-gray-500 border-gray-500';
    }
  };

  const handleItemClick = async (item: QueueItem) => {
    try {
      // Import the queue action service
      const { queueActionService } = await import('@/services/queueActionService');
      
      // Check if this is an immediate action item
      if (item.type === 'team_confirmation') {
        const result = await queueActionService.handleTeamConfirmation(item.game_id!, userId!, 'supabase');
        
        if (result.success) {
          toast.success(result.message);
          await loadQueueItems(); // Refresh queue
          if (result.redirect) {
            navigate(result.redirect);
          }
        } else {
          toast.error(result.message);
        }
        return;
      }
      
      // Handle view game details - navigate to details page
      if (item.type === 'view_game_details') {
        navigate(item.action_url);
        return;
      }

      // For other items, navigate to the action URL
      navigate(item.action_url);
    } catch (error) {
      console.error('Error handling queue item click:', error);
      toast.error('Failed to process action');
    }
  };

  const getQueuesByType = () => {
    const queues = {
      urgent: queueItems.filter(item => item.priority === 'urgent'),
      high: queueItems.filter(item => item.priority === 'high'),
      normal: queueItems.filter(item => item.priority === 'normal'),
      all: queueItems
    };
    return queues;
  };

  const queues = getQueuesByType();

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-gray-400">Loading your queue...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <QueueExplanation />
      
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">My Queue</h2>
          <p className="text-gray-400">
            {permissions.role === 'coach' ? 'Team management tasks' : 
             permissions.role === 'scorekeeper' ? 'Scoring opportunities' :
             permissions.isPlayer ? 'Game confirmations' :
             'Your action items'}
          </p>
        </div>
        <Button onClick={loadQueueItems} variant="outline" size="sm">
          Refresh
        </Button>
      </div>

      <Tabs defaultValue="urgent" className="w-full">
        <TabsList className="grid w-full grid-cols-4 bg-gray-800 border border-gray-700">
          <TabsTrigger value="urgent" className="text-white data-[state=active]:bg-red-600">
            Urgent ({queues.urgent.length})
          </TabsTrigger>
          <TabsTrigger value="high" className="text-white data-[state=active]:bg-orange-600">
            High ({queues.high.length})
          </TabsTrigger>
          <TabsTrigger value="normal" className="text-white data-[state=active]:bg-blue-600">
            Normal ({queues.normal.length})
          </TabsTrigger>
          <TabsTrigger value="all" className="text-white data-[state=active]:bg-green-600">
            All ({queues.all.length})
          </TabsTrigger>
        </TabsList>

        {['urgent', 'high', 'normal', 'all'].map(priority => (
          <TabsContent key={priority} value={priority} className="space-y-4">
            {queues[priority as keyof typeof queues].length === 0 ? (
              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="p-6 text-center">
                  <CheckCircle className="h-12 w-12 text-gray-500 mx-auto mb-4" />
                  <p className="text-gray-400 text-lg">
                    No {priority !== 'all' ? priority + ' priority' : ''} items in your queue
                  </p>
                  <p className="text-gray-500 text-sm mt-2">
                    {priority === 'urgent' ? 'No urgent actions required' :
                     priority === 'high' ? 'No high priority tasks' :
                     priority === 'normal' ? 'No normal priority tasks' :
                     'All caught up! Great work.'}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {queues[priority as keyof typeof queues].map((item) => (
                  <Card 
                    key={item.id} 
                    className="bg-gray-800 border-gray-700 hover:bg-gray-750 cursor-pointer transition-colors"
                    onClick={() => handleItemClick(item)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            {getPriorityIcon(item.priority)}
                            <h3 className="font-medium text-white">{item.title}</h3>
                            <Badge variant="outline" className={getPriorityColor(item.priority)}>
                              {item.priority}
                            </Badge>
                          </div>
                          <p className="text-gray-400 text-sm mb-2">{item.description}</p>
                          {item.due_date && (
                            <div className="flex items-center gap-2 text-xs text-gray-500">
                              <Clock className="h-3 w-3" />
                              Due: {format(new Date(item.due_date), 'MMM dd, yyyy')}
                            </div>
                          )}
                        </div>
                         {(() => {
                           const isPastGame = item.game_data?.isPastDue || item.title.includes('Missed') || item.title.includes('Past Due');
                           const isCompletedGame = item.title === 'Game Completed';
                           const isViewOnlyType = item.type === 'view_game_details';
                           
                           // For past/missed games and view-only types, show a different button
                           if (isPastGame || isViewOnlyType) {
                             return (
                               <Button 
                                 size="sm" 
                                 variant="secondary"
                                 className="bg-gray-600 text-gray-300 min-w-0 text-xs sm:text-sm px-2 sm:px-3 py-1 sm:py-2"
                               >
                                 <span className="truncate max-w-24 sm:max-w-none">
                                   {isPastGame ? 'View Details' : item.action_required}
                                 </span>
                               </Button>
                             );
                           }
                           
                           return (
                             <Button 
                               size="sm" 
                               variant={isCompletedGame ? 'secondary' : 'outline'}
                               disabled={isCompletedGame}
                               className={`${isCompletedGame 
                                 ? "bg-gray-600 text-gray-300 cursor-not-allowed" 
                                 : "hover:bg-primary hover:text-primary-foreground transition-colors"} 
                                 min-w-0 text-xs sm:text-sm px-2 sm:px-3 py-1 sm:py-2`}
                             >
                               <span className="truncate max-w-24 sm:max-w-none">
                                 {item.action_required}
                               </span>
                             </Button>
                           );
                         })()}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};
